import React, { useState, useEffect, useMemo } from "react";

/*
========================================================================================
DivorcePensionCalculator.jsx – VERSION QUÉBÉCOISE AMÉLIORÉE ET PRÉCISE

Objectif du composant : 
- Calculateur indicatif de pension alimentaire pour ex-époux au Québec,
  basé sur les "Lignes directrices facultatives" et la pratique québécoise

  1) SANS enfants :
     - La formule tient compte de:
       * Différence de revenus
       * Durée du mariage
       * Facteur progressif (1,5% à 2%) selon la durée
       * Plafonnement pour les mariages de longue durée
       * Considération de l'âge des parties et la "Règle de 65"
       * Circonstances particulières (santé, employabilité)

  2) AVEC enfants :
     - Méthode du "revenu disponible net" après calcul de la pension pour enfants
     - Fourchette de 40% à 46% selon la pratique québécoise
     - Ajustement selon les circonstances particulières et la garde

- AVERTISSEMENT IMPORTANT: Outil strictement informatif
  Les tribunaux québécois ont un pouvoir discrétionnaire important
  et peuvent s'écarter considérablement de ces estimations selon les cas.
========================================================================================
*/

// Constantes pour les calculs fiscaux (taux approximatifs par palier)
const TAX_BRACKETS_QC = [
  { threshold: 0, rate: 0.15 }, // Provincial
  { threshold: 46295, rate: 0.20 },
  { threshold: 92580, rate: 0.24 },
  { threshold: 112655, rate: 0.2575 }
];

const TAX_BRACKETS_FEDERAL = [
  { threshold: 0, rate: 0.15 }, // Fédéral
  { threshold: 50197, rate: 0.205 },
  { threshold: 100392, rate: 0.26 },
  { threshold: 155625, rate: 0.29 },
  { threshold: 221708, rate: 0.33 }
];

function DivorcePensionCalculator() {
  // -- Définition des étapes du questionnaire --
  const stepsData = [
    // ÉTAPE 0 - ADMISSIBILITÉ
    {
      id: "step0",
      title: "Admissibilité",
      shortContent: "Vérifions d'abord si vous êtes admissible à une pension alimentaire entre ex-époux",
      detailedContent:
        "Au Québec, contrairement à d'autres provinces canadiennes, seules les personnes légalement mariées " +
        "ou unies civilement peuvent demander une pension alimentaire pour ex-époux. Les conjoints de fait " +
        "(union libre) n'y sont généralement pas admissibles, sauf entente particulière.",
      questions: [
        {
          id: "q_relationStatus",
          label: "Quel était votre statut relationnel?",
          options: [
            { value: "marie", label: "Mariage légal" },
            { value: "unionCivile", label: "Union civile" },
            { value: "conjointFait", label: "Conjoint de fait (union libre)" },
          ],
          help: "Au Québec, seuls les époux mariés ou en union civile peuvent demander une pension alimentaire pour ex-époux"
        },
        {
          id: "q_prenuptialAgreement",
          label: "Aviez-vous un contrat de mariage ou une convention qui traite de pension alimentaire?",
          options: [
            { value: "oui", label: "Oui" },
            { value: "non", label: "Non" },
            { value: "incertain", label: "Je ne suis pas certain(e)" },
          ],
          help: "Les ententes préalables peuvent affecter vos droits à une pension alimentaire"
        }
      ]
    },
    
    {
      id: "step1",
      title: "Situation de base",
      shortContent: "Déterminons votre situation familiale et la durée du mariage",
      detailedContent:
        "Au Québec, le calcul de la pension alimentaire pour ex-époux varie considérablement selon que vous ayez des enfants ou non, " +
        "mais aussi selon la durée de votre mariage, qui est un facteur crucial dans les décisions des tribunaux québécois.",
      showIf: (answers) => answers["step0"]?.q_relationStatus === "marie" || 
                          (answers["step0"]?.q_relationStatus !== "conjointFait" && answers["step0"]?.q_prenuptialAgreement === "oui"),
      questions: [
        {
          id: "q_hasChildren",
          label: "Avez-vous des enfants à charge?",
          options: [
            { value: "oui", label: "Oui" },
            { value: "non", label: "Non" },
          ],
          help: "Détermine la formule de calcul applicable selon le droit québécois"
        },
        {
          id: "q_marriageDuration",
          label: "Durée du mariage ou de l'union (en années)",
          type: "text",
          help: "Inclure la période de cohabitation pertinente avant le mariage si applicable"
        },
        {
          id: "q_ageRecipient",
          label: "Âge de la personne qui pourrait recevoir la pension",
          type: "text",
          help: "L'âge influence l'autonomie économique et les décisions des tribunaux"
        },
        {
          id: "q_agePayer",
          label: "Âge de la personne qui pourrait payer la pension",
          type: "text",
          help: "Important pour considérer les perspectives de retraite et la capacité de paiement"
        }
      ],
    },

    // ÉTAPE 1.5 - SANTÉ ET EMPLOI
    {
      id: "step1_5",
      title: "Santé et employabilité",
      shortContent: "Facteurs qui pourraient influencer la durée et le montant",
      detailedContent:
        "La santé, les perspectives d'emploi et les sacrifices de carrière sont des facteurs importants " +
        "que les tribunaux québécois prennent en considération pour déterminer la pension alimentaire " +
        "entre ex-époux, tant pour le montant que pour la durée.",
      showIf: (answers) => answers["step0"]?.q_relationStatus === "marie" || 
                          (answers["step0"]?.q_relationStatus !== "conjointFait" && answers["step0"]?.q_prenuptialAgreement === "oui"),
      questions: [
        {
          id: "q_healthIssues",
          label: "La personne qui recevrait la pension a-t-elle des problèmes de santé significatifs?",
          options: [
            { value: "aucun", label: "Aucun problème de santé particulier" },
            { value: "mineurs", label: "Problèmes mineurs n'affectant pas l'employabilité" },
            { value: "moderes", label: "Problèmes modérés limitant certains types d'emplois" },
            { value: "graves", label: "Problèmes graves affectant significativement l'employabilité" },
          ],
          help: "Les problèmes de santé peuvent justifier une pension plus élevée ou plus longue"
        },
        {
          id: "q_education",
          label: "Niveau d'éducation de la personne qui recevrait la pension",
          options: [
            { value: "primaire", label: "Primaire" },
            { value: "secondaire", label: "Secondaire" },
            { value: "collegial", label: "Collégial/CÉGEP" },
            { value: "universitaire", label: "Universitaire" },
          ],
          help: "Le niveau d'éducation influence les perspectives d'emploi"
        },
        {
          id: "q_careerSacrifice",
          label: "Niveau de sacrifice professionnel pendant le mariage",
          options: [
            { value: "aucun", label: "Aucun sacrifice significatif" },
            { value: "partiel", label: "Ralentissement de carrière (temps partiel, refus de promotions)" },
            { value: "important", label: "Interruptions significatives (années sans emploi)" },
            { value: "complet", label: "Abandon complet de la carrière pour la famille" },
          ],
          help: "Les tribunaux québécois accordent une importance particulière aux sacrifices de carrière"
        },
      ]
    },

    // ÉTAPE 2 - SCÉNARIO SANS ENFANTS
    {
      id: "step2_noChildren",
      title: "Revenus des parties (sans enfant)",
      shortContent: "Calcul selon la méthode québécoise tenant compte de la durée et des revenus",
      detailedContent:
        "Dans la situation sans enfants, la pratique québécoise considère: " +
        "• La différence de revenus bruts annuels " +
        "• La durée du mariage ou de l'union " +
        "• L'âge et la capacité d'autonomie économique " +
        "• Un facteur progressif selon la durée (généralement 1,5% à 2,5% pour les mariages plus longs).",
      showIf: (answers) => {
        return answers["step1"]?.q_hasChildren === "non" && 
               (answers["step0"]?.q_relationStatus === "marie" || 
               (answers["step0"]?.q_relationStatus !== "conjointFait" && answers["step0"]?.q_prenuptialAgreement === "oui"));
      },
      questions: [
        {
          id: "q_votreRevenu",
          label: "Votre revenu annuel brut",
          type: "text",
          help: "Inclure salaire, primes, revenus de placements et autres sources de revenus"
        },
        {
          id: "q_revenuConjoint",
          label: "Revenu annuel brut de votre ex-conjoint(e)",
          type: "text",
          help: "Inclure toutes les sources de revenus"
        },
        {
          id: "q_variableIncome",
          label: "Y a-t-il des revenus variables importants (commissions, bonus)?",
          options: [
            { value: "oui", label: "Oui" },
            { value: "non", label: "Non" },
          ],
          help: "Les revenus variables peuvent être moyennés sur 3 ans"
        },
        {
          id: "q_retirementPlans",
          label: "Prévoyez-vous prendre votre retraite dans les 5 prochaines années?",
          options: [
            { value: "oui", label: "Oui" },
            { value: "non", label: "Non" },
          ],
          help: "La retraite peut influencer la durée de la pension"
        },
      ],
    },

    // ÉTAPE 2 - SCÉNARIO AVEC ENFANTS
    {
      id: "step2_children",
      title: "Revenus et situation avec enfants",
      shortContent: "Calcul selon la méthode du revenu disponible net après pension pour enfants",
      detailedContent:
        "Au Québec, lorsqu'il y a des enfants, la pension alimentaire pour ceux-ci est prioritaire. " +
        "Pour la pension entre ex-époux, le tribunal considère ensuite le revenu disponible de chacun après cette première obligation. " +
        "Les tribunaux québécois appliquent généralement une fourchette de 40% à 46% du revenu disponible global, " +
        "avec des ajustements selon les circonstances particulières.",
      showIf: (answers) => {
        return answers["step1"]?.q_hasChildren === "oui" && 
               (answers["step0"]?.q_relationStatus === "marie" || 
               (answers["step0"]?.q_relationStatus !== "conjointFait" && answers["step0"]?.q_prenuptialAgreement === "oui"));
      },
      questions: [
        {
          id: "q_revenuBrutVous",
          label: "Votre revenu annuel brut",
          type: "text",
          help: "Avant déductions et impôts"
        },
        {
          id: "q_revenuBrutConjoint",
          label: "Revenu annuel brut de votre ex-conjoint(e)",
          type: "text",
          help: "Avant déductions et impôts"
        },
        {
          id: "q_pensionEnfants",
          label: "Montant mensuel de pension alimentaire pour enfants",
          type: "text",
          help: "Selon les tables québécoises ou une entente existante"
        },
        {
          id: "q_childCustody",
          label: "Qui a la garde principale des enfants?",
          options: [
            { value: "vous", label: "Vous" },
            { value: "ex", label: "Votre ex-conjoint(e)" },
            { value: "partagee", label: "Garde partagée" },
          ],
          help: "Influence le calcul selon la pratique québécoise"
        },
        {
          id: "q_childrenAges",
          label: "Âge du plus jeune enfant",
          type: "text",
          help: "Important pour estimer la durée de l'obligation alimentaire"
        },
        {
          id: "q_specialExpenses",
          label: "Y a-t-il des dépenses particulières pour les enfants (santé, éducation)?",
          options: [
            { value: "oui", label: "Oui" },
            { value: "non", label: "Non" },
          ],
          help: "Les dépenses particulières peuvent affecter le calcul"
        },
      ],
    },

    // ÉTAPE 3 - DÉPENSES ET SITUATION FINANCIÈRE
    {
      id: "step3",
      title: "Situation financière globale",
      shortContent: "Évaluation des dépenses et du coût de la vie",
      detailedContent:
        "Les tribunaux québécois tiennent compte de la situation financière globale des parties, " +
        "incluant les dépenses essentielles et le coût de la vie dans la région. " +
        "Ces facteurs peuvent influencer l'application des lignes directrices.",
      showIf: (answers) => answers["step0"]?.q_relationStatus === "marie" || 
                          (answers["step0"]?.q_relationStatus !== "conjointFait" && answers["step0"]?.q_prenuptialAgreement === "oui"),
      questions: [
        {
          id: "q_regionCout",
          label: "Dans quelle région du Québec vivez-vous?",
          options: [
            { value: "montreal", label: "Montréal/Laval" },
            { value: "quebec", label: "Québec (capitale)" },
            { value: "urbain", label: "Autre centre urbain" },
            { value: "rural", label: "Région rurale" },
          ],
          help: "Le coût de la vie varie significativement selon les régions du Québec"
        },
        {
          id: "q_logementCout",
          label: "Coût mensuel du logement",
          type: "text",
          help: "Loyer ou hypothèque, incluant les taxes et charges"
        },
        {
          id: "q_detteImportante",
          label: "Avez-vous des dettes importantes (plus de 10 000$)?",
          options: [
            { value: "oui", label: "Oui" },
            { value: "non", label: "Non" },
          ],
          help: "Les dettes importantes peuvent affecter la capacité de paiement"
        },
        {
          id: "q_actifs",
          label: "Y a-t-il des actifs significatifs acquis pendant le mariage?",
          options: [
            { value: "oui", label: "Oui" },
            { value: "non", label: "Non" },
          ],
          help: "Les actifs peuvent influencer la décision du tribunal"
        },
      ]
    },
  ];

  // -- État pour suivre l'avancement des étapes, les réponses, etc. --
  const [completedSteps, setCompletedSteps] = useState([]);
  const [openStep, setOpenStep] = useState("step0"); // Commence par l'étape d'admissibilité
  const [answers, setAnswers] = useState({});
  const [calculationProgress, setCalculationProgress] = useState(0); // Pour la barre de progression

  // -- Calcul final (fourchette de pension) --
  const [calculatedResult, setCalculatedResult] = useState(null);
  const [showDetailedBreakdown, setShowDetailedBreakdown] = useState(false);
  const [showMultipleScenarios, setShowMultipleScenarios] = useState(false);
  const [scenarios, setScenarios] = useState([]);

  // -- État pour afficher/masquer la section d'information détaillée --
  const [showDetailedInfo, setShowDetailedInfo] = useState(false);

  // -- État pour le modal d'aide --
  const [showHelpModal, setShowHelpModal] = useState(false);

  // Calcul fiscal approximatif (simplification du code réel qui serait plus complexe)
  const calculateAfterTax = (income, pensionPaid = 0, pensionReceived = 0) => {
    // Ajustement pour la déduction/addition de pension
    const adjustedIncome = income - pensionPaid + pensionReceived;
    
    // Calcul progressif simplifié pour le Québec
    let provincialTax = 0;
    let lastThreshold = 0;
    
    for (const bracket of TAX_BRACKETS_QC) {
      if (adjustedIncome > bracket.threshold) {
        const taxableInThisBracket = Math.min(adjustedIncome, bracket.threshold) - lastThreshold;
        provincialTax += taxableInThisBracket * bracket.rate;
        lastThreshold = bracket.threshold;
      } else {
        break;
      }
    }
    
    // Calcul fédéral similaire
    let federalTax = 0;
    lastThreshold = 0;
    
    for (const bracket of TAX_BRACKETS_FEDERAL) {
      if (adjustedIncome > bracket.threshold) {
        const taxableInThisBracket = Math.min(adjustedIncome, bracket.threshold) - lastThreshold;
        federalTax += taxableInThisBracket * bracket.rate;
        lastThreshold = bracket.threshold;
      } else {
        break;
      }
    }
    
    // Déductions et abattements simplifiés
    const basicDeduction = 15000; // Approximation des déductions de base
    const abattementQC = federalTax * 0.165; // Abattement d'impôt du Québec
    
    // Impôt total approximatif
    const totalTax = provincialTax + federalTax - abattementQC;
    
    // Revenu après impôt
    return adjustedIncome - totalTax;
  };

  // Estimation de la durée de la pension basée sur des facteurs multiples
  const estimateDuration = (marriageDuration, ageRecipient, healthStatus, careerSacrifice, hasChildren, youngestChildAge) => {
    let durationEstimate = "";
    
    // Vérification de la règle de 65
    const rule65Applies = (parseFloat(marriageDuration) + parseFloat(ageRecipient) >= 65);
    
    // Mariage de longue durée ou règle de 65
    if (marriageDuration > 20 || rule65Applies) {
      durationEstimate = "durée indéterminée (possibilité de réévaluation périodique)";
    }
    // Mariage de durée moyenne
    else if (marriageDuration > 5) {
      const minYears = Math.floor(marriageDuration * 0.5);
      const maxYears = Math.ceil(marriageDuration);
      
      // Ajustements pour problèmes de santé
      if (healthStatus === "graves") {
        durationEstimate = "potentiellement de " + maxYears + " ans à durée indéterminée";
      } else {
        durationEstimate = "généralement de " + minYears + " à " + maxYears + " ans";
      }
      
      // Ajustement pour sacrifice de carrière
      if (careerSacrifice === "complet") {
        durationEstimate += ", avec possibilité de prolongation";
      }
    }
    // Mariage de courte durée
    else {
      const minMonths = Math.max(6, Math.floor(marriageDuration * 6));
      const maxYears = Math.ceil(marriageDuration/2);
      
      durationEstimate = "généralement de " + minMonths + " mois à " + maxYears + " ans";
      
      // Ajustement pour problèmes de santé
      if (healthStatus === "graves") {
        durationEstimate += ", avec possibilité de prolongation";
      }
    }
    
    // Ajustement pour enfants
    if (hasChildren === "oui" && youngestChildAge) {
      const yearsToMajority = 18 - parseFloat(youngestChildAge);
      if (yearsToMajority > 0) {
        durationEstimate += ". Considération possible jusqu'à la majorité du plus jeune enfant (encore " + yearsToMajority + " ans)";
      }
    }
    
    return durationEstimate;
  };

  // -- Recalcule dès que les réponses ou la liste des steps complétés changent --
  useEffect(() => {
    // Si aucune étape n'est complétée, pas de calcul
    if (!completedSteps.length) {
      setCalculatedResult(null);
      setCalculationProgress(0);
      return;
    }

    // Mettre à jour la progression du calcul
    const visibleSteps = getVisibleSteps();
    const completedCount = completedSteps.filter(step => 
      visibleSteps.some(vs => vs.id === step)).length;
    setCalculationProgress(Math.round((completedCount / visibleSteps.length) * 100));

    // Vérifier l'admissibilité
    if (completedSteps.includes("step0")) {
      const step0Ans = answers["step0"] || {};
      
      // Explicitly check eligibility based on status and agreement
      if (step0Ans.q_relationStatus === "conjointFait" && step0Ans.q_prenuptialAgreement !== "oui") {
        setCalculatedResult({
          type: "ineligible",
          message: "Au Québec, les conjoints de fait n'ont généralement pas droit à une pension alimentaire pour ex-époux, sauf s'il existe une entente particulière (contrat de vie commune) qui le prévoit."
        });
        return;
      }
      if (step0Ans.q_relationStatus === "unionCivile" && step0Ans.q_prenuptialAgreement !== "oui") {
         setCalculatedResult({
           type: "ineligible",
           message: "Pour les unions civiles au Québec, le droit à une pension alimentaire pour ex-époux dépend des termes de votre contrat d'union civile ou d'une entente spécifique. Sans telle entente, elle n'est généralement pas accordée."
         });
         return;
      }
    }

    // Vérifier si l'étape de base est complétée
    if (!completedSteps.includes("step1")) {
      setCalculatedResult(null);
      return;
    }

    const step1Ans = answers["step1"] || {};
    const hasChildren = step1Ans.q_hasChildren;
    const marriageDuration = parseFloat(step1Ans.q_marriageDuration || "0");
    const ageRecipient = parseFloat(step1Ans.q_ageRecipient || "0");
    const agePayer = parseFloat(step1Ans.q_agePayer || "0");

    // Vérifier les données de santé et employabilité
    const step1_5Ans = answers["step1_5"] || {};
    const healthStatus = step1_5Ans.q_healthIssues || "aucun";
    const educationLevel = step1_5Ans.q_education || "secondaire";
    const careerSacrifice = step1_5Ans.q_careerSacrifice || "aucun";
    
    // Vérifier les données de situation financière
    const step3Ans = answers["step3"] || {};
    const region = step3Ans.q_regionCout || "urbain";
    const housingCost = parseFloat(step3Ans.q_logementCout || "0");
    
    if (marriageDuration <= 0) {
      setCalculatedResult(null);
      return;
    }

    // CAS SANS ENFANTS
    if (hasChildren === "non") {
      // On vérifie si step2_noChildren est terminé AND if eligible
      if (!completedSteps.includes("step2_noChildren") || !isEligible(answers)) { 
        setCalculatedResult(null);
        return;
      }
      // Lecture des champs
      const step2Ans = answers["step2_noChildren"] || {};
      const revenuA = parseFloat(step2Ans.q_votreRevenu || "0");
      const revenuB = parseFloat(step2Ans.q_revenuConjoint || "0");
      const variableIncome = step2Ans.q_variableIncome === "oui";
      const retirementSoon = step2Ans.q_retirementPlans === "oui";

      if (revenuA <= 0 || revenuB <= 0) {
        setCalculatedResult(null);
        return;
      }

      // Différence de salaire
      const diff = Math.abs(revenuA - revenuB);
      
      // Déterminer qui est payeur et qui est receveur
      const payerIncome = Math.max(revenuA, revenuB);
      const recipientIncome = Math.min(revenuA, revenuB);
      const isPayer = revenuA > revenuB;
      
      // Facteurs québécois ajustés avec plus de précision:
      // 1. Facteur progressif selon durée - ajusté selon les LDFPAE
      let lowFactor = marriageDuration <= 5 ? 0.015 : 0.0175; // Base: 1.5% à 1.75%
      let highFactor = marriageDuration <= 5 ? 0.02 : 0.023;  // Base: 2% à 2.3%
      
      // Ajustement pour mariages plus longs (>10 ans)
      if (marriageDuration > 10) {
        lowFactor += Math.min(0.005, (marriageDuration - 10) * 0.0005);
        highFactor += Math.min(0.005, (marriageDuration - 10) * 0.0005);
      }
      
      // 2. Plafonnement pour mariages très longs (>20 ans) selon pratique québécoise
      const capFactor = marriageDuration > 20 ? 0.5 / (lowFactor * marriageDuration) : 1.0;
      
      // 3. Ajustements multiples
      let adjustmentFactor = 1.0;
      
      // Âge/autonomie - Règle de 65
      const rule65Applies = (marriageDuration + ageRecipient >= 65);
      if (rule65Applies) {
        adjustmentFactor *= 1.15; // +15% pour règle de 65
      } else if (ageRecipient > 50) {
        adjustmentFactor *= (1 + Math.min(0.15, (ageRecipient - 50) * 0.01)); // +1% par année après 50 ans, max +15%
      }
      
      // Santé
      if (healthStatus === "moderes") {
        adjustmentFactor *= 1.1; // +10% pour problèmes modérés
      } else if (healthStatus === "graves") {
        adjustmentFactor *= 1.2; // +20% pour problèmes graves
      }
      
      // Sacrifice de carrière
      if (careerSacrifice === "partiel") {
        adjustmentFactor *= 1.1; // +10% pour sacrifice partiel
      } else if (careerSacrifice === "important") {
        adjustmentFactor *= 1.15; // +15% pour sacrifice important
      } else if (careerSacrifice === "complet") {
        adjustmentFactor *= 1.2; // +20% pour sacrifice complet
      }
      
      // Calcul final avec tous les facteurs
      const base = diff * Math.min(marriageDuration, 25); // Plafonnement à 25 ans dans le calcul
      const lowAnnual = lowFactor * base * capFactor * adjustmentFactor;
      const highAnnual = highFactor * base * capFactor * adjustmentFactor;
      
      // Conversion en mensuel
      const lowMonthly = lowAnnual / 12;
      const highMonthly = highAnnual / 12;
      
      // Estimation de durée selon pratique québécoise
      const durationEstimate = estimateDuration(marriageDuration, ageRecipient, healthStatus, careerSacrifice, hasChildren);
      
      // Ajustement fiscal approximatif
      const lowAfterTax = calculateAfterTax(recipientIncome, lowMonthly * 12);
      const highAfterTax = calculateAfterTax(recipientIncome, highMonthly * 12);

      setCalculatedResult({
        type: "noChildren",
        diffSalaire: diff.toFixed(2),
        marriageDuration,
        ageRecipient,
        basAnnuel: lowAnnual.toFixed(2),
        hautAnnuel: highAnnual.toFixed(2),
        basMensuel: lowMonthly.toFixed(2),
        hautMensuel: highMonthly.toFixed(2),
        basApresImpot: lowAfterTax.toFixed(2),
        hautApresImpot: highAfterTax.toFixed(2),
        durationEstimate
      });
    }

    // CAS AVEC ENFANTS
    if (hasChildren === "oui") {
      // On vérifie si step2_children est terminé AND if eligible
      if (!completedSteps.includes("step2_children") || !isEligible(answers)) { 
        setCalculatedResult(null);
        return;
      }
      const step2Ans = answers["step2_children"] || {};
      const revVous = parseFloat(step2Ans.q_revenuBrutVous || "0");
      const revConjoint = parseFloat(step2Ans.q_revenuBrutConjoint || "0");
      const pensionEnfants = parseFloat(step2Ans.q_pensionEnfants || "0");
      const custody = step2Ans.q_childCustody || "";
      const youngestChildAge = parseFloat(step2Ans.q_childrenAges || "0");
      const specialExpenses = step2Ans.q_specialExpenses === "oui";

      if (revVous <= 0 || revConjoint <= 0) {
        setCalculatedResult(null);
        return;
      }

      // Ajustement du facteur selon garde et durée du mariage
      let lowFactorPercent = 40; // 40% base
      let highFactorPercent = 46; // 46% base
      
      // Ajustements selon circonstances spécifiques au Québec
      if (custody === "vous" && revVous < revConjoint) {
        // Si receveur a garde principale, pourcentage plus élevé selon pratique québécoise
        lowFactorPercent += 2;
        highFactorPercent += 2;
      }
      
      if (marriageDuration > 15) {
        // Mariage long = fourchette légèrement plus élevée
        lowFactorPercent += Math.min(3, (marriageDuration - 15) * 0.3);
        highFactorPercent += Math.min(2, (marriageDuration - 15) * 0.2);
      }
      
      // Ajustement pour âge (selon l'avocat, facteur très important au Québec)
      if (ageRecipient > 50) {
        lowFactorPercent += Math.min(2, (ageRecipient - 50) * 0.1);
        highFactorPercent += Math.min(2, (ageRecipient - 50) * 0.1);
      }

      // Revenu disponible total
      const total = revVous + revConjoint - pensionEnfants;
      
      // Application des pourcentages ajustés
      const bas = total * (lowFactorPercent / 100);
      const haut = total * (highFactorPercent / 100);

      // Ajustement pour obtenir la pension (pas juste le revenu disponible global)
      const pension = revConjoint > revVous 
        ? Math.max(0, bas - revVous)  // Si receveur = vous
        : Math.max(0, bas - revConjoint); // Si receveur = ex-conjoint
      
      const pensionHaute = revConjoint > revVous
        ? Math.max(0, haut - revVous)
        : Math.max(0, haut - revConjoint);

      setCalculatedResult({
        type: "withChildren",
        revenuGlobal: total.toFixed(2),
        lowPercent: lowFactorPercent.toFixed(1),
        highPercent: highFactorPercent.toFixed(1),
        pension: pension.toFixed(2),
        pensionHaute: pensionHaute.toFixed(2),
        basMensuel: (pension / 12).toFixed(2),
        hautMensuel: (pensionHaute / 12).toFixed(2),
        durationEstimate: estimateDuration(marriageDuration, ageRecipient, healthStatus, careerSacrifice, hasChildren, youngestChildAge)
      });
    }
  }, [answers, completedSteps]);

  // Helper function to check eligibility based on Step 0 answers
  const isEligible = (currentAnswers) => {
    const step0Ans = currentAnswers["step0"] || {};
    if (step0Ans.q_relationStatus === "marie") {
      return true;
    }
    if ((step0Ans.q_relationStatus === "unionCivile" || step0Ans.q_relationStatus === "conjointFait") && step0Ans.q_prenuptialAgreement === "oui") {
      return true;
    }
    return false;
  };

  // Gère la saisie de réponse pour les inputs
  const handleAnswerChange = (stepId, questionId, value) => {
    setAnswers((prev) => ({
      ...prev,
      [stepId]: { ...prev[stepId], [questionId]: value },
    }));
  };

  // Vérifie si toutes les questions d'une étape sont remplies
  const areAllQuestionsAnswered = (step) => {
    if (!step.questions?.length) return true;
    const stepAns = answers[step.id] || {};
    return step.questions.every((q) => {
      if (q.options || q.type === "text") {
        return stepAns[q.id] && stepAns[q.id] !== "";
      }
      return true;
    });
  };

  // Demande confirmation de la fin d'une étape
  const confirmStepCompletion = (step) => {
    return window.confirm(`Confirmez-vous avoir terminé l'étape "${step.title}" ?`);
  };

  // Filtre pour n'afficher que les étapes "visibles" (showIf)
  const getVisibleSteps = () => {
    return stepsData.filter((st) => {
      if (typeof st.showIf === "function") {
        return st.showIf(answers) !== false;
      }
      return true;
    });
  };

  // Marque l'étape comme terminée
  const markStepAsDone = (step) => {
    // On vérifie d'abord si toutes les questions sont répondues
    if (!areAllQuestionsAnswered(step)) {
      alert("Veuillez répondre à toutes les questions de cette étape avant de la terminer.");
      return;
    }

    // Vérifie que les étapes précédentes sont déjà terminées
    const visible = getVisibleSteps();
    const idx = visible.findIndex((s) => s.id === step.id);
    const previous = visible.slice(0, idx);
    for (const ps of previous) {
      if (!completedSteps.includes(ps.id)) {
        alert(`Veuillez d'abord compléter l'étape « ${ps.title} »`);
        return;
      }
    }

    // Confirmation
    if (!confirmStepCompletion(step)) return;

    // On enregistre l'étape comme terminée
    if (!completedSteps.includes(step.id)) {
      setCompletedSteps((prev) => [...prev, step.id]);
    }
    setOpenStep(null);
  };

  // Bouton de réinitialisation
  const resetAll = () => {
    setCompletedSteps([]);
    setOpenStep(null);
    setAnswers({});
    setCalculatedResult(null);
    setCalculationProgress(0);
    setShowDetailedBreakdown(false);
    setShowMultipleScenarios(false);
    setScenarios([]);
    setShowHelpModal(false);
  };

  // -- Composant pour le Modal d'Aide --
  const HelpModal = ({ isOpen, onClose }) => {
    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 transition-opacity duration-300 ease-in-out">
        <div className="bg-slate-50 rounded-xl border border-slate-300 shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col transform transition-all duration-300 ease-in-out scale-95 opacity-0 animate-modalshow">
          {/* En-tête du modal */}
          <div className="flex justify-between items-center p-5 border-b border-slate-200 bg-gradient-to-r from-blue-600 to-blue-500 rounded-t-xl">
            <h2 className="text-xl font-semibold text-white flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 mr-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
              </svg>
              Comment fonctionne notre calculateur ?
            </h2>
            <button 
              onClick={onClose} 
              className="text-white hover:text-blue-100 transition-colors p-2 rounded-full hover:bg-blue-600"
              aria-label="Fermer"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Corps du modal avec scroll */}
          <div className="p-6 overflow-y-auto">
            {/* Introduction avec boîte dédiée */}
            <div className="bg-white rounded-lg p-5 mb-6 shadow-sm border border-slate-200">
              <h3 className="text-lg font-semibold text-blue-700 mb-3">Bienvenue dans notre calculateur</h3>
              <p className="text-slate-700">
                Cet outil vous donne une <strong>estimation</strong> de la pension alimentaire entre ex-époux au Québec.
                Il ne remplace pas les conseils d'un avocat, mais peut vous aider à mieux comprendre les montants possibles.
              </p>
            </div>

            {/* Grille de cartes pour les concepts principaux */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
              {/* Carte 1: Les deux approches */}
              <div className="bg-white rounded-lg p-5 shadow-sm border border-slate-200 hover:shadow-md transition-shadow duration-300">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="bg-blue-100 text-blue-600 p-2 rounded-full">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                    </svg>
                  </div>
                  <h4 className="text-lg font-semibold text-blue-700">Deux approches distinctes</h4>
                </div>
                <ul className="space-y-3">
                  <li className="flex items-start space-x-2">
                    <span className="text-blue-500 font-bold mt-0.5">A.</span>
                    <div>
                      <span className="font-medium">Sans enfants:</span> Calcul basé sur la différence de revenus et la durée du mariage.
                    </div>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-blue-500 font-bold mt-0.5">B.</span>
                    <div>
                      <span className="font-medium">Avec enfants:</span> Priorité à la pension pour enfants, puis analyse du revenu disponible restant.
                    </div>
                  </li>
                </ul>
              </div>

              {/* Carte 2: Les variables clés */}
              <div className="bg-white rounded-lg p-5 shadow-sm border border-slate-200 hover:shadow-md transition-shadow duration-300">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="bg-blue-100 text-blue-600 p-2 rounded-full">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                    </svg>
                  </div>
                  <h4 className="text-lg font-semibold text-blue-700">Variables clés</h4>
                </div>
                <ul className="text-slate-700 space-y-2">
                  <li>• Revenus bruts annuels des deux parties</li>
                  <li>• Durée du mariage (en années)</li>
                  <li>• Âge des deux ex-époux</li>
                  <li>• Sacrifices de carrière durant le mariage</li>
                  <li>• État de santé et employabilité</li>
                  <li>• Garde et âge des enfants (si applicable)</li>
                </ul>
              </div>
            </div>

            {/* Visualisation des formules */}
            <div className="grid grid-cols-1 gap-5 mb-6">
              {/* Formule sans enfants */}
              <div className="bg-gradient-to-br from-blue-50 to-slate-50 rounded-lg p-5 shadow-sm border border-slate-200">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="bg-blue-100 text-blue-600 p-2 rounded-full">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h4 className="text-lg font-semibold text-blue-700">Méthode de calcul: Sans enfants</h4>
                </div>
                
                <div className="bg-white rounded-lg p-4 mb-3 border border-slate-200">
                  <div className="font-mono text-sm text-center">
                    <span className="bg-blue-100 p-1 rounded text-blue-800">Pension annuelle</span> = 
                    <span className="bg-green-100 p-1 rounded text-green-800 mx-1">Différence de revenus</span> × 
                    <span className="bg-yellow-100 p-1 rounded text-yellow-800 mx-1">Durée du mariage</span> × 
                    <span className="bg-red-100 p-1 rounded text-red-800 mx-1">Facteur (1,5% à 2,3%)</span> × 
                    <span className="bg-purple-100 p-1 rounded text-purple-800 mx-1">Ajustements</span>
                  </div>
                </div>
                
                <div className="space-y-2 text-slate-700">
                  <p><strong>Ajustements importants:</strong></p>
                  <ul className="list-disc list-inside ml-2 space-y-1 text-sm">
                    <li>Règle de 65: Si (âge + durée mariage) ≥ 65, ajout de ~15%</li>
                    <li>Âge: +1% par année au-dessus de 50 ans (max 15%)</li>
                    <li>Santé/carrière: +10% à +20% selon les sacrifices</li>
                  </ul>
                </div>
              </div>
              
              {/* Formule avec enfants */}
              <div className="bg-gradient-to-br from-blue-50 to-slate-50 rounded-lg p-5 shadow-sm border border-slate-200">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="bg-blue-100 text-blue-600 p-2 rounded-full">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                    </svg>
                  </div>
                  <h4 className="text-lg font-semibold text-blue-700">Méthode de calcul: Avec enfants</h4>
                </div>
                
                <div className="bg-white rounded-lg p-4 mb-3 border border-slate-200">
                  <div className="text-sm space-y-3">
                    <p className="flex items-center">
                      <span className="w-8 h-8 flex items-center justify-center bg-blue-100 text-blue-800 rounded-full font-bold mr-3">1</span>
                      <span><strong>Revenu disponible net</strong> = Revenu total - Pension enfants</span>
                    </p>
                    <p className="flex items-center">
                      <span className="w-8 h-8 flex items-center justify-center bg-blue-100 text-blue-800 rounded-full font-bold mr-3">2</span>
                      <span><strong>Objectif:</strong> 40% à 46% de ce revenu pour le bénéficiaire</span>
                    </p>
                    <p className="flex items-center">
                      <span className="w-8 h-8 flex items-center justify-center bg-blue-100 text-blue-800 rounded-full font-bold mr-3">3</span>
                      <span><strong>Ajustements:</strong> garde principale (+2%), mariage long (+0-3%), âge (+0-2%)</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Diagramme interactif */}
            <div className="bg-white rounded-lg p-5 shadow-sm border border-slate-200 mb-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="bg-blue-100 text-blue-600 p-2 rounded-full">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                  </svg>
                </div>
                <h4 className="text-lg font-semibold text-blue-700">Comment les facteurs interagissent</h4>
              </div>
              
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1 bg-blue-50 p-3 rounded-lg border border-blue-100 flex flex-col items-center justify-center text-center">
                    <span className="font-semibold text-blue-700 mb-2">Durée du mariage</span>
                    <span className="text-sm text-slate-600">Pilier fondamental<br/>Plus long = pension plus élevée/plus longue</span>
                  </div>
                  <div className="flex-1 bg-green-50 p-3 rounded-lg border border-green-100 flex flex-col items-center justify-center text-center">
                    <span className="font-semibold text-green-700 mb-2">Différence de revenus</span>
                    <span className="text-sm text-slate-600">Écart plus grand<br/>= pension plus importante</span>
                  </div>
                  <div className="flex-1 bg-red-50 p-3 rounded-lg border border-red-100 flex flex-col items-center justify-center text-center">
                    <span className="font-semibold text-red-700 mb-2">Âge</span>
                    <span className="text-sm text-slate-600">+50 ans ou Règle de 65<br/>= pension potentiellement augmentée</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Section limitations */}
            <div className="bg-red-50 rounded-lg p-5 shadow-sm border border-red-200 mb-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="bg-red-100 text-red-600 p-2 rounded-full">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h4 className="text-lg font-semibold text-red-700">Limitations importantes</h4>
              </div>
              
              <div className="space-y-3">
                <div className="bg-white p-3 rounded-lg border border-red-100 text-red-800 font-semibold text-center">
                  C'EST UNE ESTIMATION, PAS UNE CERTITUDE
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div className="bg-white p-3 rounded-lg border border-red-100">
                    <p className="font-semibold mb-1">Le juge a le pouvoir</p>
                    <p className="text-slate-700">Les tribunaux peuvent décider d'un montant très différent selon les détails uniques de votre situation.</p>
                  </div>
                  <div className="bg-white p-3 rounded-lg border border-red-100">
                    <p className="font-semibold mb-1">Calculs d'impôt simplifiés</p>
                    <p className="text-slate-700">Les calculs d'impôt "après pension" sont approximatifs et simplifiés.</p>
                  </div>
                  <div className="bg-white p-3 rounded-lg border border-red-100">
                    <p className="font-semibold mb-1">Ne couvre pas tout</p>
                    <p className="text-slate-700">Partage des biens, dettes importantes, situations particulières ne sont pas intégrés.</p>
                  </div>
                  <div className="bg-white p-3 rounded-lg border border-red-100">
                    <p className="font-semibold mb-1">Pas de conseils juridiques</p>
                    <p className="text-slate-700">Cet outil ne remplace JAMAIS la consultation d'un avocat. Chaque cas est unique.</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Origine méthode */}
            <div className="bg-white rounded-lg p-5 shadow-sm border border-slate-200 mb-6">
              <div className="flex items-center space-x-3 mb-3">
                <div className="bg-blue-100 text-blue-600 p-2 rounded-full">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <h4 className="text-lg font-semibold text-blue-700">D'où vient cette méthode?</h4>
              </div>
              <ul className="text-slate-700 space-y-2">
                <li>• S'inspire des <a href="https://www.justice.gc.ca/fra/pr-rp/lf-fl/epoux-spousal/calc/orrssagc.pdf" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline"><strong>"Lignes directrices facultatives en matière de pensions alimentaires pour époux"</strong></a> (guide non-législatif du gouvernement fédéral).</li>
                <li>• Reflète la pratique générale observée dans les tribunaux québécois.</li>
                <li>• Applicable uniquement aux mariages légaux (les unions civiles et conjoints de fait ont des règles différentes).</li>
              </ul>
            </div>
            
            {/* Conclusion */}
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-5 shadow-sm">
              <p className="text-center text-white">
                Utilisez ce calculateur comme un point de départ pour réfléchir et discuter.<br/>
                <strong className="font-bold">Pour des conseils précis adaptés à votre situation, consultez un professionnel du droit.</strong>
              </p>
            </div>
          </div>
            
          <style jsx global>{`
            .animate-modalshow {
              animation: modalShow 0.3s ease-out forwards;
            }
            @keyframes modalShow {
              from {
                opacity: 0;
                transform: scale(0.95) translateY(-20px);
              }
              to {
                opacity: 1;
                transform: scale(1) translateY(0);
              }
            }
          `}</style>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="px-6 py-8 mt-4 bg-blue-50 rounded-xl border border-blue-100 shadow-sm mb-8">
          <h1 className="text-2xl font-bold text-blue-700 mb-3 text-center">
            <span className="inline-block h-1 bg-warning w-8 mr-2 rounded-full align-middle"></span>
            Calculateur de pension alimentaire pour ex-époux
            <span className="inline-block h-1 bg-warning w-8 ml-2 rounded-full align-middle"></span>
          </h1>
          <p className="text-center text-blue-600 max-w-3xl mx-auto">
            Estimation indicative basée sur la pratique québécoise et les Lignes directrices facultatives.
          </p>
        </div>

        {/* Top 3 cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {/* Card 1: Progress */}
          <div className="card bg-white border border-slate-200 shadow-sm p-6 transition-all duration-200 hover:shadow-md">
            <h3 className="font-semibold text-blue-700 mb-3 flex items-center">
              <span className="bg-blue-50 p-2 rounded-full mr-2">📊</span>
              Progression
            </h3>
            <div className="mb-4">
              <div className="flex justify-between text-sm mb-1">
                <span className="font-semibold">{calculationProgress}% complété</span>
                <span className="text-slate-500 text-xs">
                  {completedSteps.length} / {getVisibleSteps().length} étapes
                </span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-3">
                <div
                  className="bg-blue-500 h-3 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${calculationProgress}%` }}
                ></div>
              </div>
            </div>
          </div>

          {/* Card 2: Calculation Status */}
          <div className="card bg-white border border-slate-200 shadow-sm p-6 transition-all duration-200 hover:shadow-md">
            <h3 className="font-semibold text-blue-700 mb-3 flex items-center">
              <span className="bg-blue-50 p-2 rounded-full mr-2">💡</span>
              Statut du calcul
            </h3>
            <div className="flex items-center justify-center h-16">
              {calculatedResult ? (
                <div className="text-center">
                  <p className="text-sm text-green-600">Calcul effectué</p>
                  <p className="text-xs text-slate-600 mt-1">Voir résultats ci-dessous</p>
                </div>
              ) : (
                <p className="text-slate-500 text-center text-sm">
                  Complétez les étapes pour voir l'estimation
                </p>
              )}
            </div>
          </div>

          {/* Card 3: Actions */}
          <div className="card bg-white border border-slate-200 shadow-sm p-6 transition-all duration-200 hover:shadow-md">
            <h3 className="font-semibold text-blue-700 mb-3 flex items-center">
              <span className="bg-blue-50 p-2 rounded-full mr-2">🔄</span>
              Actions
            </h3>
            <div className="flex justify-center items-center h-16">
              <button
                onClick={resetAll}
                className="btn btn-outline border-red-300 text-red-700 hover:bg-red-50 hover:border-red-400"
              >
                Réinitialiser tout
              </button>
            </div>
          </div>
        </div>

        {/* Résultat du calcul */}
        {calculatedResult && (
          <div className="mb-8">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <h2 className="text-xl font-semibold text-blue-700 mb-6 flex items-center">
                <span className="bg-blue-50 p-2 rounded-full mr-2">📋</span>
                Estimation de la pension alimentaire
              </h2>

              {calculatedResult.type === "ineligible" ? (
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                  <p className="text-yellow-700">{calculatedResult.message}</p>
                </div>
              ) : (
                <>
                  {/* Cas sans enfants */}
                  {calculatedResult.type === "noChildren" && (
                    <div className="space-y-6">
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <h3 className="font-semibold text-blue-700 mb-2">Facteurs considérés</h3>
                        <ul className="space-y-2 text-sm">
                          <li>• Différence de revenus bruts: <strong>{calculatedResult.diffSalaire} $</strong></li>
                          <li>• Durée du mariage: <strong>{calculatedResult.marriageDuration} ans</strong></li>
                          <li>• Âge du bénéficiaire: <strong>{calculatedResult.ageRecipient} ans</strong></li>
                        </ul>
                      </div>

                      <div className="bg-green-50 p-4 rounded-lg">
                        <h3 className="font-semibold text-green-700 mb-2">Fourchette estimée</h3>
                        <ul className="space-y-2 text-sm">
                          <li>• Pension mensuelle: <strong>{calculatedResult.basMensuel} $ à {calculatedResult.hautMensuel} $</strong></li>
                          <li>• Après impôts (approx.): <strong>{calculatedResult.basApresImpot} $ à {calculatedResult.hautApresImpot} $</strong></li>
                          <li>• Durée potentielle: <strong>{calculatedResult.durationEstimate}</strong></li>
                        </ul>
                      </div>
                    </div>
                  )}

                  {/* Cas avec enfants */}
                  {calculatedResult.type === "withChildren" && (
                    <div className="space-y-6">
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <h3 className="font-semibold text-blue-700 mb-2">Facteurs considérés</h3>
                        <ul className="space-y-2 text-sm">
                          <li>• Revenu disponible total: <strong>{calculatedResult.revenuGlobal} $</strong></li>
                          <li>• Fourchette applicable: <strong>{calculatedResult.lowPercent}% à {calculatedResult.highPercent}%</strong></li>
                        </ul>
                      </div>

                      <div className="bg-green-50 p-4 rounded-lg">
                        <h3 className="font-semibold text-green-700 mb-2">Fourchette estimée</h3>
                        <ul className="space-y-2 text-sm">
                          <li>• Pension mensuelle: <strong>{calculatedResult.basMensuel} $ à {calculatedResult.hautMensuel} $</strong></li>
                          <li>• Durée potentielle: <strong>{calculatedResult.durationEstimate}</strong></li>
                        </ul>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* Timeline steps */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-4 bg-blue-100 text-blue-700 flex justify-between items-center">
            <div>
              <h2 className="text-lg font-bold flex items-center">
                <span className="mr-3 text-2xl">📝</span>
                Questionnaire interactif
              </h2>
              <p className="text-sm mt-1">
                Répondez aux questions pour obtenir une estimation de la pension alimentaire.
              </p>
            </div>
            <button 
              onClick={() => setShowHelpModal(true)} 
              className="flex items-center gap-x-2 px-4 py-2 rounded-full text-sm font-medium text-blue-700 bg-blue-200 hover:bg-blue-300 border border-blue-300 shadow-sm transition-colors"
              title="Comment fonctionne ce calculateur ?"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.755 4 3.92C16 13.099 14.828 14 12.803 14c-.798 0-1.573-.313-2.172-.844m-1.573-3.929A3.874 3.874 0 0112.185 3.799a3.874 3.874 0 01.001 7.748M12 18v.01" />
              </svg>
              Aide
            </button>
          </div>

          <div className="p-6">
            <div className="relative">
              <div className="absolute bg-slate-200 w-[3px] h-full left-4 md:left-6 top-0 z-0" />
              <div className="space-y-6">
                {getVisibleSteps().map((step, index) => {
                  const isDone = completedSteps.includes(step.id);
                  const isOpen = openStep === step.id;
                  const canOpen = getVisibleSteps()
                    .slice(0, index)
                    .every((s) => completedSteps.includes(s.id));

                  return (
                    <div key={step.id} className="flex flex-col md:flex-row relative z-10">
                      <div className="mr-6">
                        <div
                          className={`w-8 h-8 rounded-full border-4 ${
                            isDone ? "border-green-500 bg-green-50" : "border-slate-400 bg-white"
                          } flex items-center justify-center text-sm font-bold transition-colors`}
                          style={{ marginLeft: "-2px" }}
                        >
                          {index + 1}
                        </div>
                      </div>
                      <div className="flex-1">
                        <div
                          className={`card border shadow-sm transition-all ${
                            isDone ? "bg-green-50 border-green-200" : "bg-white border-slate-200"
                          } hover:shadow-md`}
                        >
                          <div className="card-body p-6">
                            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                              <h2
                                className={`card-title text-base ${
                                  isDone ? "text-green-700" : "text-slate-700"
                                }`}
                              >
                                {step.title}
                              </h2>
                            </div>
                            <p className="mt-2 text-sm text-slate-600">{step.shortContent}</p>

                            <div className="mt-3 flex gap-3 flex-wrap">
                              <button
                                className={`btn btn-sm ${
                                  isOpen ? "btn-neutral" : "btn-primary"
                                } ${!canOpen ? "opacity-50 cursor-not-allowed" : ""}`}
                                onClick={() => canOpen && setOpenStep(isOpen ? null : step.id)}
                                disabled={!canOpen}
                              >
                                {isOpen ? "Masquer détails" : "Voir détails"}
                              </button>
                            </div>

                            {/* Questions panel */}
                            {isOpen && (
                              <div className="mt-4 p-4 border rounded-lg bg-slate-50 space-y-4">
                                <div
                                  className="text-sm text-slate-600"
                                  dangerouslySetInnerHTML={{ __html: step.detailedContent }}
                                />

                                {step.questions?.length > 0 && (
                                  <div className="space-y-6 mt-4">
                                    {step.questions.map((q) => {
                                      if (q.showIf && !q.showIf(answers)) return null;

                                      const stepAns = answers[step.id] || {};
                                      const selectedValue = stepAns[q.id] || "";

                                      if (q.options) {
                                        return (
                                          <div key={q.id} className="space-y-2">
                                            <label className="font-medium text-slate-700 block">
                                              {q.label}
                                            </label>
                                            {q.help && (
                                              <p className="text-xs text-slate-500">{q.help}</p>
                                            )}
                                            <div className="ml-4 space-y-2">
                                              {q.options.map((opt) => (
                                                <label
                                                  key={opt.value}
                                                  className="cursor-pointer flex items-center gap-2 hover:bg-slate-100 p-2 rounded-md transition-colors"
                                                >
                                                  <input
                                                    type="radio"
                                                    className="radio radio-primary"
                                                    name={`${step.id}-${q.id}`}
                                                    value={opt.value}
                                                    checked={selectedValue === opt.value}
                                                    onChange={() =>
                                                      handleAnswerChange(step.id, q.id, opt.value)
                                                    }
                                                  />
                                                  <span className="text-slate-700">{opt.label}</span>
                                                </label>
                                              ))}
                                            </div>
                                          </div>
                                        );
                                      }

                                      if (q.type === "text") {
                                        return (
                                          <div key={q.id} className="space-y-2">
                                            <label className="font-medium text-slate-700 block">
                                              {q.label}
                                            </label>
                                            {q.help && (
                                              <p className="text-xs text-slate-500">{q.help}</p>
                                            )}
                                            <input
                                              type="text"
                                              inputMode="numeric"
                                              pattern="[0-9]*"
                                              className="input input-bordered w-full max-w-xs"
                                              value={selectedValue}
                                              onChange={(e) => {
                                                // N'accepter que les chiffres et le point décimal
                                                const value = e.target.value.replace(/[^0-9.]/g, '');
                                                handleAnswerChange(step.id, q.id, value);
                                              }}
                                            />
                                          </div>
                                        );
                                      }

                                      return null;
                                    })}
                                  </div>
                                )}
                                
                                {/* Boutons de validation placés à la fin des questions */}
                                <div className="mt-6 flex gap-3 flex-wrap">
                                  {!isDone && (
                                    <button
                                      className="btn btn-sm btn-success hover:bg-green-600 transition-colors"
                                      onClick={() => markStepAsDone(step)}
                                    >
                                      Terminer l'étape
                                    </button>
                                  )}
                                  {isDone && (
                                    <span className="text-green-700 font-semibold self-center text-sm flex items-center">
                                      <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                      </svg>
                                      Terminée
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Legal note */}
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-sm text-slate-600 mt-8">
          <p className="italic text-center">
            Note : Ce calculateur est fourni à titre informatif. Les tribunaux québécois ont un pouvoir discrétionnaire important 
            et peuvent s'écarter considérablement de ces estimations. Pour des conseils juridiques adaptés à votre situation, 
            consultez un professionnel du droit.
          </p>
        </div>

        {/* Information détaillée (repliable) */}
        <div className="mt-8">
          <button
            onClick={() => setShowDetailedInfo(!showDetailedInfo)}
            className="btn btn-sm btn-outline w-full"
          >
            {showDetailedInfo ? "Masquer l'information détaillée" : "Afficher l'information détaillée"}
          </button>

          {showDetailedInfo && (
            <div className="mt-4 p-6 bg-white rounded-xl border border-slate-200 shadow-sm">
              <h2 className="text-xl font-semibold text-blue-700 mb-4">Informations complémentaires</h2>
              <div className="prose prose-sm max-w-none">
                <h3>Cadre juridique québécois</h3>
                <p>
                  Au Québec, la pension alimentaire entre ex-époux est régie par la Loi sur le divorce (fédérale) 
                  et le Code civil du Québec. Les tribunaux disposent d'un important pouvoir d'appréciation.
                </p>

                <h3>Facteurs déterminants</h3>
                <ul>
                  <li>Durée du mariage</li>
                  <li>Écart de revenus</li>
                  <li>Âge et état de santé</li>
                  <li>Sacrifices de carrière</li>
                </ul>

                <h3>Considérations fiscales</h3>
                <p>
                  La pension alimentaire pour ex-époux est déductible d'impôt pour le payeur et imposable 
                  pour le bénéficiaire, contrairement à la pension pour enfants.
                </p>
              </div>
            </div>
          )}
        </div>
      </main>
      <HelpModal isOpen={showHelpModal} onClose={() => setShowHelpModal(false)} />
    </div>
  );
}

export default DivorcePensionCalculator;
